import { z } from 'zod'

// ============================================
// STORY-039: Types pour l'API Qonto
// ============================================

// Configuration OAuth2 Qonto
export interface QontoConfig {
  clientId: string
  clientSecret: string
  redirectUri: string
  accessToken?: string
  refreshToken?: string
  expiresAt?: number
}

// Compte bancaire Qonto
export const qontoBankAccountSchema = z.object({
  slug: z.string(),
  iban: z.string(),
  bic: z.string(),
  currency: z.string().default('EUR'),
  balance: z.number(),
  balance_cents: z.number(),
  authorized_balance: z.number(),
  authorized_balance_cents: z.number(),
  name: z.string(),
  updated_at: z.string(),
})

export type QontoBankAccount = z.infer<typeof qontoBankAccountSchema>

// Transaction Qonto
export const qontoTransactionSchema = z.object({
  transaction_id: z.string(),
  amount: z.number(),
  amount_cents: z.number(),
  currency: z.string().default('EUR'),
  local_amount: z.number(),
  local_amount_cents: z.number(),
  local_currency: z.string(),
  side: z.enum(['credit', 'debit']),
  operation_type: z.enum([
    'transfer',
    'card',
    'direct_debit',
    'income',
    'qonto_fee',
    'cheque',
    'recall',
    'swift_income',
  ]),
  status: z.enum(['pending', 'declined', 'completed', 'reversed']),
  settled_at: z.string().nullable(),
  emitted_at: z.string(),
  updated_at: z.string(),
  label: z.string(),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  counterparty: z.string().nullable(),
  initiator_id: z.string().nullable(),
  category: z.string().nullable(),
  attachment_ids: z.array(z.string()).default([]),
  vat_amount: z.number().nullable(),
  vat_amount_cents: z.number().nullable(),
  vat_rate: z.number().nullable(),
})

export type QontoTransaction = z.infer<typeof qontoTransactionSchema>

// ============================================
// STORY-040: Types pour la synchronisation
// ============================================

export type SyncStatus = 'pending' | 'synced' | 'matched' | 'failed' | 'ignored'
export type MatchConfidence = 'high' | 'medium' | 'low' | 'manual'

// Transaction synchronisee en base
export const syncedTransactionSchema = z.object({
  id: z.string().uuid(),
  qonto_transaction_id: z.string(),
  amount_cents: z.number(),
  currency: z.string().default('EUR'),
  side: z.enum(['credit', 'debit']),
  operation_type: z.string(),
  status: z.enum(['pending', 'declined', 'completed', 'reversed']),
  label: z.string(),
  reference: z.string().nullable(),
  counterparty: z.string().nullable(),
  emitted_at: z.string(),
  settled_at: z.string().nullable(),
  sync_status: z.enum(['pending', 'synced', 'matched', 'failed', 'ignored']).default('synced'),
  matched_invoice_id: z.string().uuid().nullable(),
  match_confidence: z.enum(['high', 'medium', 'low', 'manual']).nullable(),
  match_reason: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

export type SyncedTransaction = z.infer<typeof syncedTransactionSchema>

// Log de synchronisation
export const syncLogSchema = z.object({
  id: z.string().uuid(),
  sync_type: z.enum(['manual', 'scheduled', 'webhook']),
  started_at: z.string(),
  completed_at: z.string().nullable(),
  status: z.enum(['running', 'success', 'partial', 'failed']),
  transactions_fetched: z.number().default(0),
  transactions_new: z.number().default(0),
  transactions_matched: z.number().default(0),
  transactions_failed: z.number().default(0),
  error_message: z.string().nullable(),
  created_at: z.string(),
})

export type SyncLog = z.infer<typeof syncLogSchema>

// Resultat de matching
export interface MatchResult {
  transactionId: string
  invoiceId: string | null
  confidence: MatchConfidence
  reason: string
  amountMatch: boolean
  referenceMatch: boolean
  dateProximity: number // jours
}

// ============================================
// STORY-041: Types pour la detection d'impayes
// ============================================

export type UnpaidAlertLevel = 'warning' | 'danger' | 'critical'
export type UnpaidAlertStatus = 'active' | 'acknowledged' | 'resolved' | 'escalated'

// Alerte d'impaye
export const unpaidAlertSchema = z.object({
  id: z.string().uuid(),
  invoice_id: z.string().uuid(),
  alert_level: z.enum(['warning', 'danger', 'critical']),
  alert_status: z.enum(['active', 'acknowledged', 'resolved', 'escalated']),
  days_overdue: z.number(),
  amount_due_cents: z.number(),
  client_id: z.string().uuid(),
  last_reminder_sent_at: z.string().nullable(),
  reminder_count: z.number().default(0),
  next_action_at: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

export type UnpaidAlert = z.infer<typeof unpaidAlertSchema> & {
  invoice?: {
    id: string
    numero: string
    total_ttc: number
    date_echeance: string
    client?: {
      id: string
      full_name: string
      email: string
      company?: string
    }
  }
}

// Configuration des seuils d'alerte
export interface UnpaidAlertThresholds {
  warningDays: number // Jours apres echeance pour warning (ex: 7)
  dangerDays: number // Jours apres echeance pour danger (ex: 30)
  criticalDays: number // Jours apres echeance pour critical (ex: 60)
  autoReminder: boolean // Envoyer des relances auto
  reminderIntervalDays: number // Intervalle entre relances
  maxReminders: number // Nombre max de relances
}

// ============================================
// STORY-042: Types pour les paiements recurrents
// ============================================

export type RecurrenceFrequency = 'monthly' | 'quarterly' | 'biannual' | 'annual'
export type RecurringPaymentStatus = 'active' | 'paused' | 'cancelled' | 'completed'

// Contrat/Abonnement recurrent (utilise la table `contracts` dans Supabase)
export const contractSchema = z.object({
  id: z.string().uuid(),
  client_id: z.string().uuid(),
  title: z.string().min(1, 'Le titre est requis'),
  description: z.string().nullable(),
  amount_ht_cents: z.number(),
  tva_rate: z.number().default(20),
  frequency: z.enum(['monthly', 'quarterly', 'biannual', 'annual']),
  start_date: z.string(),
  end_date: z.string().nullable(),
  next_invoice_date: z.string(),
  last_invoice_date: z.string().nullable(),
  last_invoice_id: z.string().uuid().nullable(),
  status: z.enum(['active', 'paused', 'cancelled', 'completed']).default('active'),
  auto_generate: z.boolean().default(true),
  days_before_due: z.number().default(15), // Generer X jours avant echeance
  invoice_template: z.object({
    lignes: z.array(z.object({
      description: z.string(),
      quantite: z.number(),
      prix_unitaire: z.number(),
      taux_tva: z.number(),
    })),
    conditions: z.string().nullable(),
  }).nullable(),
  notes_internes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
})

export type Contract = z.infer<typeof contractSchema> & {
  client?: {
    id: string
    full_name: string
    email: string
    company?: string
  }
  invoices_generated?: number
  total_invoiced_cents?: number
}

export type ContractFormData = Omit<
  z.infer<typeof contractSchema>,
  'id' | 'created_at' | 'updated_at' | 'last_invoice_date' | 'last_invoice_id'
>

// Alias pour compatibilité avec le code existant
export const recurringContractSchema = contractSchema
export type RecurringContract = Contract
export type RecurringContractFormData = ContractFormData

// Historique des factures generees (utilise la table `contract_invoice_history` dans Supabase)
export const contractInvoiceHistorySchema = z.object({
  id: z.string().uuid(),
  contract_id: z.string().uuid(),
  invoice_id: z.string().uuid(),
  period_start: z.string(),
  period_end: z.string(),
  generated_at: z.string(),
  status: z.enum(['generated', 'sent', 'paid', 'cancelled']),
})

export type ContractInvoiceHistory = z.infer<typeof contractInvoiceHistorySchema>

// Alias pour compatibilité avec le code existant
export const recurringInvoiceHistorySchema = contractInvoiceHistorySchema
export type RecurringInvoiceHistory = ContractInvoiceHistory

// ============================================
// Statistiques et Filtres
// ============================================

export interface QontoStats {
  totalBalance: number
  pendingTransactions: number
  syncedTransactions: number
  matchedTransactions: number
  lastSyncAt: string | null
}

export interface AccountingStats {
  unpaidTotal: number
  unpaidCount: number
  overdueTotal: number
  overdueCount: number
  recurringMonthlyRevenue: number
  activeContracts: number
  alertsCount: {
    warning: number
    danger: number
    critical: number
  }
}

export interface TransactionFilters {
  search?: string
  dateFrom?: string
  dateTo?: string
  side?: 'credit' | 'debit'
  status?: SyncStatus
  matched?: boolean
  page?: number
  limit?: number
}

export interface UnpaidFilters {
  search?: string
  alertLevel?: UnpaidAlertLevel
  alertStatus?: UnpaidAlertStatus
  minDaysOverdue?: number
  page?: number
  limit?: number
}

export interface RecurringContractFilters {
  search?: string
  status?: RecurringPaymentStatus
  frequency?: RecurrenceFrequency
  clientId?: string
  page?: number
  limit?: number
}

// Labels et couleurs
export const frequencyLabels: Record<RecurrenceFrequency, string> = {
  monthly: 'Mensuel',
  quarterly: 'Trimestriel',
  biannual: 'Semestriel',
  annual: 'Annuel',
}

export const recurringStatusLabels: Record<RecurringPaymentStatus, string> = {
  active: 'Actif',
  paused: 'En pause',
  cancelled: 'Annule',
  completed: 'Termine',
}

export const recurringStatusColors: Record<RecurringPaymentStatus, { bg: string; text: string }> = {
  active: { bg: 'bg-mint-100', text: 'text-mint-800' },
  paused: { bg: 'bg-gold-100', text: 'text-gold-800' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800' },
  completed: { bg: 'bg-beige-100', text: 'text-beige-800' },
}

export const alertLevelLabels: Record<UnpaidAlertLevel, string> = {
  warning: 'Attention',
  danger: 'Urgent',
  critical: 'Critique',
}

export const alertLevelColors: Record<UnpaidAlertLevel, { bg: string; text: string; icon: string }> = {
  warning: { bg: 'bg-gold-100', text: 'text-gold-800', icon: 'text-gold-600' },
  danger: { bg: 'bg-orange-100', text: 'text-orange-800', icon: 'text-orange-600' },
  critical: { bg: 'bg-red-100', text: 'text-red-800', icon: 'text-red-600' },
}

export const syncStatusLabels: Record<SyncStatus, string> = {
  pending: 'En attente',
  synced: 'Synchronise',
  matched: 'Rapproche',
  failed: 'Echec',
  ignored: 'Ignore',
}

export const syncStatusColors: Record<SyncStatus, { bg: string; text: string }> = {
  pending: { bg: 'bg-gray-100', text: 'text-gray-800' },
  synced: { bg: 'bg-blue-100', text: 'text-blue-800' },
  matched: { bg: 'bg-mint-100', text: 'text-mint-800' },
  failed: { bg: 'bg-red-100', text: 'text-red-800' },
  ignored: { bg: 'bg-beige-100', text: 'text-beige-800' },
}
